/*
 * RootLoader is the app's first-paint loading screen. It renders BEFORE the
 * AtomsProvider / BricksProvider (and before hydration), so it is intentionally
 * plain React with raw HTML elements and inline styles: atoms/bricks primitives
 * depend on provider context that is not mounted yet at this point, and adding
 * them to the critical first-paint path would bloat it. Keep this file
 * framework-light and self-contained — do NOT migrate it to atoms components or
 * Tailwind-only styling; raw HTML + inline styles are correct here.
 */
/* eslint-disable @opendoor/no-html-elements, react/forbid-dom-props, react/forbid-component-props -- pre-provider first-paint loader; intentionally raw HTML + inline styles (see note above) */
import type { ReactNode } from 'react';
import { startTransition, useCallback, useEffect, useRef, useState } from 'react';

import { globalTrackEvent, useLoadObservability } from 'platform/observability';
import { useHydrated } from 'platform/router/utils/useHydrated';
import { matchPath, useLocation, useNavigation } from 'react-router';
import { IS_PROD } from 'shared/helpers/env';
import { MAPBOX_ACCESS_TOKEN } from 'shared/helpers/globals';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { useIsomorphicLayoutEffect } from 'shared/hooks/useIsomorphicLayoutEffect';
import { normalizePathname } from 'shared/hooks/usePath';

import {
  getRouteContentCommitKey,
  LOADER_STUCK_WARNING_MS,
  useLoaderContext,
} from './LoaderContext';
import { OpendoorArchSpinner } from './OpendoorArchSpinner';
import { OpendoorLogo } from './OpendoorLogo';
import type { RootLoaderAddressPreviewDetail } from './rootLoaderAddressPreviewUtils';
import {
  extractRootLoaderAddressUuid,
  ROOT_LOADER_ADDRESS_PREVIEW_EVENT,
} from './rootLoaderAddressPreviewUtils';

export const ROOT_LOADER_APP_CONTENTS_ID = 'cake-app-contents';
export const ROOT_LOADER_ID = 'root-loader';
export const ROOT_LOADER_DONE_EVENT = 'cake:root-loader-done';
export const ROOT_LOADER_VISIBLE_EVENT = 'cake:root-loader-visible';
// Stable attribute on each RootLoaderSurface div so the pre-hydration CSS in
// root.tsx has a real production selector to target (not a data-testid).
export const ROOT_LOADER_SURFACE_ATTR = 'data-cake-loader-surface';
// Attribute the pre-hydration script in root.tsx sets on <html> to force the
// contextual surface visible before React takes over. RootLoader removes it
// once mounted so React drives visibility from that point on.
export const ROOT_LOADER_INITIAL_SURFACE_ATTR = 'data-cake-loader-initial-surface';

const rootLoaderStages = [
  'Pulling local market data',
  'Reviewing recent home sales',
  'Checking trends in your area',
  'Bringing it all together',
  'Almost ready',
];

const rootLoaderColors = {
  brand: 'var(--color-contentAccentBrand, #0041E7)',
  content: 'var(--color-contentPrimary, #23201d)',
  inactiveContent: 'var(--color-contentStateInactivePrimary, rgba(33, 32, 31, 0.35))',
  inactiveBrand: 'var(--color-contentStateInactiveAccentBrand, rgba(0, 65, 231, 0.35))',
  surface: 'var(--color-backgroundPrimary, #ffffff)',
  mutedSurface: 'var(--color-backgroundSecondary, #f9f8f8)',
  mapPlaceholder: 'var(--color-backgroundTertiary, #e8e6e4)',
} as const;

const rootLoaderEase = 'cubic-bezier(0.65, 0, 0.35, 1)';
const rootLoaderAddressEase = 'cubic-bezier(0.22, 1, 0.36, 1)';
const rootLoaderFontFamily = '"Graphik", system-ui, -apple-system, "Segoe UI", sans-serif';
const rootLoaderContentWidth = 320;
const rootLoaderContentVisualInset = 24;
const rootLoaderAddressRevealDelay = 280;
const rootLoaderAddressContentRevealDelay = 260;
const rootLoaderMetricsVersion = 'handoff_loader_v1';
const rootLoaderSurfaceTransition = `opacity 420ms ease-out, transform 560ms ${rootLoaderAddressEase}`;
const rootLoaderSkeletonMask =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='310' height='87' viewBox='0 0 310 87'%3E%3Crect x='0' y='0' width='114' height='87' rx='8' fill='white'/%3E%3Crect x='130' y='22' width='180' height='16' rx='8' fill='white'/%3E%3Crect x='130' y='50' width='140' height='16' rx='8' fill='white'/%3E%3C/svg%3E\")";
const rootLoaderStylesheet = `
        #${ROOT_LOADER_ID}, #${ROOT_LOADER_ID} * {
          font-family: ${rootLoaderFontFamily};
        }
        #${ROOT_LOADER_ID} { color: ${rootLoaderColors.content}; }
        #${ROOT_LOADER_ID} p, #${ROOT_LOADER_ID} h1 { margin: 0; }
        @font-face {
          font-family: "Graphik";
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url("https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2") format("woff2");
        }
        @font-face {
          font-family: "Graphik";
          font-style: normal;
          font-weight: 500;
          font-display: swap;
          src: url("https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2") format("woff2");
        }
        @keyframes cake-root-loader-dot-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 65, 231, 0); }
          18% { box-shadow: 0 0 4px 6px rgba(0, 65, 231, 0.15); }
          42% { box-shadow: 0 0 4px 2px rgba(0, 65, 231, 0.15); }
          68% { box-shadow: 0 0 4px 6px rgba(0, 65, 231, 0.15); }
          88% { box-shadow: 0 0 4px 3px rgba(0, 65, 231, 0.15); }
          100% { box-shadow: 0 0 0 0 rgba(0, 65, 231, 0); }
        }
        #root-loader-address-skeleton .root-loader-skeleton-piece {
          position: relative;
          background: ${rootLoaderColors.mapPlaceholder};
          box-shadow: 0 0 0 1px rgba(121, 118, 117, 0.26);
        }
        #root-loader-address-skeleton::after {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              135deg,
              transparent 0%,
              rgba(206, 194, 192, 0) 25%,
              rgba(206, 194, 192, 0.85) 50%,
              rgba(206, 194, 192, 0) 75%,
              transparent 100%
            );
          background-repeat: no-repeat;
          background-size: 180px 180px;
          filter: blur(12px);
          animation: cake-root-loader-skeleton-shimmer 1800ms ease-in-out infinite;
          pointer-events: none;
          -webkit-mask:
            ${rootLoaderSkeletonMask}
            0 0 / 310px 87px no-repeat;
          mask:
            ${rootLoaderSkeletonMask}
            0 0 / 310px 87px no-repeat;
        }
        @keyframes cake-root-loader-skeleton-shimmer {
          0% { background-position: -220px -120px; }
          100% { background-position: 360px 120px; }
        }
        @keyframes cake-root-loader-content-in {
          0% { opacity: 0; transform: translateY(3px) scale(0.995); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cake-root-loader-contextual-surface-in {
          0% { opacity: 0; transform: translateY(10px) scale(0.995); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cake-root-loader-checklist-slide {
          0%      { transform: translateY(0); animation-timing-function: linear; }
          10.11%  { transform: translateY(0); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          14.04%  { transform: translateY(-39px); animation-timing-function: linear; }
          32.58%  { transform: translateY(-39px); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          36.52%  { transform: translateY(-78px); animation-timing-function: linear; }
          55.06%  { transform: translateY(-78px); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          58.99%  { transform: translateY(-117px); animation-timing-function: linear; }
          77.53%  { transform: translateY(-117px); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          81.46%  { transform: translateY(-156px); animation-timing-function: linear; }
          100%    { transform: translateY(-156px); }
        }
        @keyframes cake-root-loader-checklist-fill {
          0%      { height: 0px; animation-timing-function: linear; }
          13.48%  { height: 0px; animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          15.73%  { height: 39px; animation-timing-function: linear; }
          35.96%  { height: 39px; animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          38.20%  { height: 78px; animation-timing-function: linear; }
          58.43%  { height: 78px; animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          60.67%  { height: 117px; animation-timing-function: linear; }
          80.90%  { height: 117px; animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          83.15%  { height: 156px; animation-timing-function: linear; }
          100%    { height: 156px; }
        }
        @keyframes cake-root-loader-checklist-dot-fill {
          from {
            background: ${rootLoaderColors.mutedSurface};
            border-color: ${rootLoaderColors.inactiveBrand};
          }
          to {
            background: ${rootLoaderColors.brand};
            border-color: rgba(255, 255, 255, 0.35);
          }
        }
        .cake-root-loader-checklist-slider {
          animation: cake-root-loader-checklist-slide 17800ms forwards;
        }
        .cake-root-loader-checklist-fill-bar {
          animation: cake-root-loader-checklist-fill 17800ms forwards;
        }
        .cake-root-loader-checklist-dot {
          background: ${rootLoaderColors.mutedSurface};
          border: 1.5px solid ${rootLoaderColors.inactiveBrand};
          animation-name: cake-root-loader-checklist-dot-fill;
          animation-duration: 400ms;
          animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
          animation-fill-mode: forwards;
          animation-play-state: paused;
        }
        .cake-root-loader-checklist-dot--row-0 { animation-play-state: running; animation-delay: 0ms; }
        .cake-root-loader-checklist-dot--row-1 { animation-play-state: running; animation-delay: 2400ms; }
        .cake-root-loader-checklist-dot--row-2 { animation-play-state: running; animation-delay: 6400ms; }
        .cake-root-loader-checklist-dot--row-3 { animation-play-state: running; animation-delay: 10400ms; }
        .cake-root-loader-checklist-dot--row-4 { animation-play-state: running; animation-delay: 14400ms; }
        @media (prefers-reduced-motion: reduce) {
          #${ROOT_LOADER_ID},
          #${ROOT_LOADER_ID} *,
          #${ROOT_LOADER_ID} *::before,
          #${ROOT_LOADER_ID} *::after {
            animation-duration: 1ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 1ms !important;
            scroll-behavior: auto !important;
          }
          #root-loader-address-skeleton::after {
            animation: none !important;
            opacity: 0;
          }
        }
      `;
const rootLoaderStylesheetMarkup = {
  // <style> is a raw-text element; SSR-escaped quotes remain literal CSS text.
  __html: rootLoaderStylesheet,
} as const;

type RootLoaderPhase = 'running' | 'fading' | 'gone';

type RootLoaderAddressPreviewStatus = 'not_applicable' | 'pending' | 'preview' | 'unavailable';

type RootLoaderBlocker =
  | 'address_reveal'
  | 'hydration'
  | 'hydration_effects'
  | 'loader_entries'
  | 'route_content_commit';

const rootLoaderBlockerNames: ReadonlySet<string> = new Set<RootLoaderBlocker>([
  'address_reveal',
  'hydration',
  'hydration_effects',
  'loader_entries',
  'route_content_commit',
]);

type RootLoaderCoords = { lat: number; lng: number };

type RootLoaderAddressLines = {
  localityLine: string;
  streetLine: string;
};

type RootLoaderMetricAction = 'abandoned' | 'address_preview' | 'hidden' | 'map_loaded' | 'shown';

type RootLoaderMetricPayload = Record<string, boolean | number | string | undefined>;

function getRootLoaderMetricLabel(action: RootLoaderMetricAction): string {
  return `root-loader-${action.replaceAll('_', '-')}`;
}

function getRootLoaderMetricPath(pathname: string): string {
  return normalizePathname(pathname) || 'root';
}

function getRootLoaderMetricSurface(pathname: string): string {
  const [surface = 'root'] = getRootLoaderMetricPath(pathname).split('/');
  return surface || 'root';
}

function getRootLoaderDurationMs(startedAt: number | null): number | undefined {
  if (startedAt == null) {
    return undefined;
  }
  return Math.round(performance.now() - startedAt);
}

function getRootLoaderMetricList(values: Iterable<string>): string {
  const serializedValues = Array.from(values).filter(Boolean).toSorted().join(',');
  return serializedValues || 'none';
}

function getRootLoaderBlockersFromMetricList(value: string): RootLoaderBlocker[] {
  if (value === 'none') {
    return [];
  }
  return value
    .split(',')
    .filter((blocker): blocker is RootLoaderBlocker => rootLoaderBlockerNames.has(blocker));
}

function getRootLoaderBlockers({
  entriesSize,
  hasCurrentRouteContentCommitted,
  hydrated,
  hydrationEffectsSettled,
  isAddressRevealPending,
}: {
  entriesSize: number;
  hasCurrentRouteContentCommitted: boolean;
  hydrated: boolean;
  hydrationEffectsSettled: boolean;
  isAddressRevealPending: boolean;
}): RootLoaderBlocker[] {
  const blockers: RootLoaderBlocker[] = [];
  if (!hydrated) blockers.push('hydration');
  if (!hydrationEffectsSettled) blockers.push('hydration_effects');
  if (!hasCurrentRouteContentCommitted) blockers.push('route_content_commit');
  if (entriesSize > 0) blockers.push('loader_entries');
  if (isAddressRevealPending) blockers.push('address_reveal');
  return blockers;
}

function trackRootLoaderAction(
  action: RootLoaderMetricAction,
  payload: RootLoaderMetricPayload,
): void {
  globalTrackEvent(
    'loading-duration',
    getRootLoaderMetricLabel(action),
    {
      data: {
        loader_event: action,
        loader_name: 'root_loader',
        loader_version: rootLoaderMetricsVersion,
        ...payload,
      },
    },
    action === 'hidden' || action === 'abandoned' ? { addTiming: true } : undefined,
  );
}

function getRootLoaderCoords(
  preview: RootLoaderAddressPreviewDetail | null,
): RootLoaderCoords | null {
  return preview?.lat != null &&
    preview.lng != null &&
    Number.isFinite(preview.lat) &&
    Number.isFinite(preview.lng)
    ? { lat: preview.lat, lng: preview.lng }
    : null;
}

function getRootLoaderAddressLines(address: string | null): RootLoaderAddressLines {
  if (!address) {
    return { localityLine: '', streetLine: '' };
  }

  const [streetLine = '', ...localityParts] = address.split(/,\s*/);
  return {
    localityLine: localityParts.join(', '),
    streetLine,
  };
}

function getRootLoaderMapUrl(coords: RootLoaderCoords | null): string | null {
  if (!coords) {
    return null;
  }

  const mapImagePath = `https://api.mapbox.com/styles/v1/opendoor/cktkqasmc1gbe17pfo2cm7ybb/static/${coords.lng},${coords.lat},15,0/228x174@2x`;
  const params = new URLSearchParams({ access_token: MAPBOX_ACCESS_TOKEN });
  return `${mapImagePath}?${params}`;
}

function isRootLoaderAddressPreviewForAddress(
  preview: RootLoaderAddressPreviewDetail | null,
  addressUUID: string | null,
): boolean {
  if (!preview) {
    return true;
  }
  if (!addressUUID) {
    return !preview.addressUUID;
  }
  return preview.addressUUID === addressUUID;
}

function getRootLoaderAddressPreviewStatus(
  preview: RootLoaderAddressPreviewDetail | null,
  addressUUID: string | null,
): RootLoaderAddressPreviewStatus {
  if (preview) {
    return 'preview';
  }
  if (addressUUID) {
    return 'unavailable';
  }
  return 'not_applicable';
}

function isCustomEvent<T>(event: Event): event is CustomEvent<T> {
  return 'detail' in event;
}

function useRootLoaderAddressPreview(addressUUID: string | null): {
  addressPreview: RootLoaderAddressPreviewDetail | null;
  addressPreviewStatus: RootLoaderAddressPreviewStatus;
  mapLoaded: boolean;
  setMapLoaded: (loaded: boolean) => void;
} {
  const [addressPreview, setAddressPreview] = useState<RootLoaderAddressPreviewDetail | null>(null);
  const [addressPreviewStatus, setAddressPreviewStatus] = useState<RootLoaderAddressPreviewStatus>(
    addressUUID ? 'pending' : 'not_applicable',
  );
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setAddressPreview(null);
      setAddressPreviewStatus(addressUUID ? 'pending' : 'not_applicable');
      setMapLoaded(false);
    });
  }, [addressUUID]);

  useEffect(() => {
    const handleAddressPreview = (event: Event) => {
      if (!isCustomEvent<RootLoaderAddressPreviewDetail | null>(event)) return;

      const preview = event.detail;
      if (preview && !isRootLoaderAddressPreviewForAddress(preview, addressUUID)) return;

      startTransition(() => {
        setAddressPreview(preview);
        setAddressPreviewStatus(getRootLoaderAddressPreviewStatus(preview, addressUUID));
        setMapLoaded(false);
      });
    };

    window.addEventListener(ROOT_LOADER_ADDRESS_PREVIEW_EVENT, handleAddressPreview);
    return () => {
      window.removeEventListener(ROOT_LOADER_ADDRESS_PREVIEW_EVENT, handleAddressPreview);
    };
  }, [addressUUID]);

  return { addressPreview, addressPreviewStatus, mapLoaded, setMapLoaded };
}

function useRootLoaderAddressReveal(
  addressUUID: string | null,
  hasAddress: boolean,
): {
  isAddressContentRevealed: boolean;
} {
  const [revealedAddressContentUUID, setRevealedAddressContentUUID] = useState<string | null>(null);

  useEffect(() => {
    if (!addressUUID || !hasAddress) {
      setRevealedAddressContentUUID(null);
      return undefined;
    }

    if (revealedAddressContentUUID === addressUUID) {
      return undefined;
    }

    const contentTimeoutId = window.setTimeout(() => {
      startTransition(() => setRevealedAddressContentUUID(addressUUID));
    }, rootLoaderAddressRevealDelay + rootLoaderAddressContentRevealDelay);
    return () => {
      window.clearTimeout(contentTimeoutId);
    };
  }, [addressUUID, hasAddress, revealedAddressContentUUID]);

  return {
    isAddressContentRevealed: Boolean(addressUUID && revealedAddressContentUUID === addressUUID),
  };
}

function RootLoaderAddressRow({
  ariaHidden,
  children,
  id,
  reserveChecklistGap = false,
}: {
  ariaHidden?: boolean;
  children: ReactNode;
  id?: string;
  reserveChecklistGap?: boolean;
}) {
  return (
    <div
      id={id}
      aria-hidden={ariaHidden}
      style={{
        height: 87,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: reserveChecklistGap ? 32 : 0,
        position: 'relative',
      }}
    >
      {children}
    </div>
  );
}

function RootLoaderAddressMedia({ children }: { children?: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width: 114,
        height: 87,
        borderRadius: 8,
        background: rootLoaderColors.mapPlaceholder,
        boxShadow: '0 0 0 1px rgba(121, 118, 117, 0.26)',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function RootLoaderMapPreview({
  coords,
  mapLoaded,
  mapUrl,
  onMapLoad,
}: {
  coords: RootLoaderCoords | null;
  mapLoaded: boolean;
  mapUrl: string | null;
  onMapLoad: () => void;
}) {
  return (
    <RootLoaderAddressMedia>
      {mapUrl && (
        <img
          src={mapUrl}
          alt=""
          onLoad={onMapLoad}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: mapLoaded ? 1 : 0,
            transition: 'opacity 220ms ease-out',
          }}
        />
      )}
      {coords && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: rootLoaderColors.brand,
            border: `2px solid ${rootLoaderColors.surface}`,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)',
            opacity: mapLoaded ? 1 : 0,
            transition: 'opacity 220ms ease-out',
          }}
        />
      )}
    </RootLoaderAddressMedia>
  );
}

function RootLoaderPropertyPreview({
  localityLine,
  streetLine,
  coords,
  mapLoaded,
  mapUrl,
  onMapLoad,
}: {
  localityLine: string;
  streetLine: string;
  coords: RootLoaderCoords | null;
  mapLoaded: boolean;
  mapUrl: string | null;
  onMapLoad: () => void;
}) {
  return (
    <div
      id="root-loader-property-preview"
      style={{
        height: 119,
      }}
    >
      <RootLoaderAddressRow>
        <RootLoaderMapPreview
          coords={coords}
          mapLoaded={mapLoaded}
          mapUrl={mapUrl}
          onMapLoad={onMapLoad}
        />
        <p
          id="root-loader-address"
          aria-label={[streetLine, localityLine].filter(Boolean).join(', ')}
          style={{
            minWidth: 0,
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: rootLoaderFontFamily,
            fontWeight: 500,
            fontSize: 16,
            lineHeight: 1.25,
            letterSpacing: 0,
            color: rootLoaderColors.content,
          }}
        >
          <span
            style={{
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {streetLine}
          </span>
          {localityLine ? (
            <span
              style={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {localityLine}
            </span>
          ) : null}
        </p>
      </RootLoaderAddressRow>
    </div>
  );
}

function RootLoaderAddressSkeleton() {
  return (
    <RootLoaderAddressRow id="root-loader-address-skeleton" ariaHidden reserveChecklistGap>
      <RootLoaderAddressMedia />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <span
          className="root-loader-skeleton-piece"
          style={{
            display: 'block',
            width: 180,
            height: 16,
            borderRadius: 8,
          }}
        />
        <span
          className="root-loader-skeleton-piece"
          style={{
            display: 'block',
            width: 140,
            height: 16,
            borderRadius: 8,
          }}
        />
      </div>
    </RootLoaderAddressRow>
  );
}

function RootLoaderChecklist() {
  const visibleRows = 3;
  const rowHeight = 39;
  const dotSize = 10;
  const lineWidth = 2;
  const listLength = rootLoaderStages.length * visibleRows;
  const viewportHeight = rowHeight * visibleRows;
  const rowCenter = rowHeight / 2;
  const lineOffset = dotSize / 2 - lineWidth / 2;

  return (
    <div
      aria-hidden="true"
      style={{ position: 'relative', alignSelf: 'stretch', height: viewportHeight }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
        }}
      >
        <div
          className="cake-root-loader-checklist-slider"
          style={{ position: 'absolute', left: 0, right: 0, top: rowHeight }}
        >
          <div
            style={{
              position: 'absolute',
              left: lineOffset,
              top: rowCenter,
              width: lineWidth,
              height: (listLength - 1) * rowHeight,
              background: 'rgba(0, 65, 231, 0.18)',
              borderRadius: 1,
            }}
          />
          <div
            className="cake-root-loader-checklist-fill-bar"
            style={{
              position: 'absolute',
              left: lineOffset,
              top: rowCenter,
              width: lineWidth,
              background: rootLoaderColors.brand,
              borderRadius: 1,
            }}
          />

          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {Array.from({ length: listLength }, (_, i) => {
              const label = rootLoaderStages[i % rootLoaderStages.length];
              const rowIndex = i % rootLoaderStages.length;
              const dotClass =
                i < rootLoaderStages.length
                  ? `cake-root-loader-checklist-dot cake-root-loader-checklist-dot--row-${rowIndex}`
                  : 'cake-root-loader-checklist-dot';
              return (
                <li
                  key={i}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: i * rowHeight,
                    height: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexShrink: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: dotSize,
                      height: dotSize,
                    }}
                  >
                    <span
                      className={dotClass}
                      style={{
                        width: dotSize,
                        height: dotSize,
                        borderRadius: '50%',
                        boxSizing: 'border-box',
                        display: 'block',
                      }}
                    />
                  </span>
                  <span
                    style={{
                      marginLeft: dotSize,
                      whiteSpace: 'nowrap',
                      fontFamily: rootLoaderFontFamily,
                      fontWeight: 400,
                      fontSize: 16,
                      lineHeight: 1.25,
                      letterSpacing: 0,
                      color: rootLoaderColors.inactiveContent,
                    }}
                  >
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function RootLoaderSimpleState() {
  return (
    <div
      id="root-loader-simple-state"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <OpendoorArchSpinner />
    </div>
  );
}

function RootLoaderSurface({
  children,
  isVisible,
  id,
  variant,
}: {
  children: ReactNode;
  isVisible: boolean;
  id: string;
  variant: 'contextual' | 'simple';
}) {
  const hiddenTransform =
    variant === 'contextual' ? 'translateY(10px) scale(0.995)' : 'translateY(-8px) scale(0.995)';
  let animation: string | undefined;
  if (isVisible && variant === 'contextual') {
    animation = `cake-root-loader-contextual-surface-in 520ms ${rootLoaderAddressEase} both`;
  }

  return (
    <div
      aria-hidden={!isVisible}
      id={id}
      {...{ [ROOT_LOADER_SURFACE_ATTR]: variant }}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : hiddenTransform,
        transition: rootLoaderSurfaceTransition,
        animation,
        pointerEvents: isVisible ? 'auto' : 'none',
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}

export function RootLoader() {
  useLoadObservability();

  const hydrated = useHydrated();
  const [hydrationEffectsSettled, setHydrationEffectsSettled] = useState(false);
  const [phase, setPhase] = useState<RootLoaderPhase>('running');
  const [hasShownLoaderBefore, setHasShownLoaderBefore] = useState(false);
  const hiddenTrackedRef = useRef(false);
  const sawAddressPreviewRef = useRef(false);
  const sawAddressUnavailableRef = useRef(false);
  const sawMapLoadedRef = useRef(false);
  const sawBlockersRef = useRef<Set<RootLoaderBlocker>>(new Set());
  const shownAtRef = useRef<number | null>(null);
  const { entries, routeContentCommitKey } = useLoaderContext();
  const location = useLocation();
  const navigation = useNavigation();
  const effectivePathname = navigation.location?.pathname ?? location.pathname;
  const currentRouteContentCommitKey = getRouteContentCommitKey(location);
  const hasCurrentRouteContentCommitted = routeContentCommitKey === currentRouteContentCommitKey;
  const isSellDashboardPath = !!matchPath(
    { path: getHubSellingRoute(), end: false },
    effectivePathname,
  );
  // useNavigation().location and useLocation().pathname can briefly disagree
  // during the hydration handoff on a hard load — isSellDashboardPath flips
  // false for one render, which would crossfade contextual → simple mid-session
  // (both surfaces stay mounted, so opacity transitions fire). Latch the surface
  // choice for the current loader session; reset when phase='gone' below so a
  // subsequent in-session re-show re-evaluates cleanly.
  const sessionShowContextualSellLoaderRef = useRef<boolean | null>(null);
  // oxlint-disable-next-line react-hooks-js/refs -- Session-latch pattern: initializing the ref only on the first render is intentional so the contextual/simple choice stays stable across hydration handoff.
  if (sessionShowContextualSellLoaderRef.current === null) {
    sessionShowContextualSellLoaderRef.current = isSellDashboardPath;
  }
  // oxlint-disable-next-line react-hooks-js/refs -- Reading the latched session value during render is the whole point of the session-latch ref above.
  const showContextualSellLoader = sessionShowContextualSellLoaderRef.current;
  const loaderVisualMode = showContextualSellLoader ? 'sell_contextual' : 'simple';
  const addressUUID = isSellDashboardPath ? extractRootLoaderAddressUuid(effectivePathname) : null;
  const { addressPreview, addressPreviewStatus, mapLoaded, setMapLoaded } =
    useRootLoaderAddressPreview(addressUUID);
  const { localityLine, streetLine } = getRootLoaderAddressLines(addressPreview?.address ?? null);
  const hasAddress = streetLine.length > 0;
  const { isAddressContentRevealed } = useRootLoaderAddressReveal(addressUUID, hasAddress);
  const isAddressRevealPending = Boolean(
    showContextualSellLoader && addressUUID && hasAddress && !isAddressContentRevealed,
  );
  const isLoading =
    !hydrated ||
    !hydrationEffectsSettled ||
    !hasCurrentRouteContentCommitted ||
    entries.size > 0 ||
    isAddressRevealPending;
  const isWaitingForRouteContentCommit =
    hydrated && hydrationEffectsSettled && !hasCurrentRouteContentCommitted && entries.size === 0;
  const blockers = getRootLoaderBlockers({
    entriesSize: entries.size,
    hasCurrentRouteContentCommitted,
    hydrated,
    hydrationEffectsSettled,
    isAddressRevealPending,
  });
  const blockersKey = getRootLoaderMetricList(blockers);
  const loaderKeys = getRootLoaderMetricList(entries.keys());
  const routePath = getRootLoaderMetricPath(location.pathname);
  const routeSurface = getRootLoaderMetricSurface(location.pathname);
  const coords = getRootLoaderCoords(addressPreview);
  const mapUrl = getRootLoaderMapUrl(coords);
  const finishFade = useCallback(() => {
    startTransition(() => {
      setPhase((current) => (current === 'fading' ? 'gone' : current));
    });
  }, []);
  const buildRootLoaderMetricPayload = useCallback(
    (payload?: RootLoaderMetricPayload): RootLoaderMetricPayload => ({
      active_loader_count: entries.size,
      active_loader_keys: loaderKeys,
      address_state: addressPreviewStatus,
      blockers: blockersKey,
      duration_ms: getRootLoaderDurationMs(shownAtRef.current),
      has_address_uuid: Boolean(addressUUID),
      is_sell_dashboard_path: isSellDashboardPath,
      loader_visual_mode: loaderVisualMode,
      route_path: routePath,
      route_surface: routeSurface,
      ...payload,
    }),
    [
      addressPreviewStatus,
      addressUUID,
      blockersKey,
      entries.size,
      isSellDashboardPath,
      loaderKeys,
      loaderVisualMode,
      routePath,
      routeSurface,
    ],
  );

  useIsomorphicLayoutEffect(() => {
    if (hydrated) {
      setHydrationEffectsSettled(true);
    }
  }, [hydrated]);

  useIsomorphicLayoutEffect(() => {
    startTransition(() => {
      if (isLoading) {
        setPhase('running');
      } else {
        setPhase((current) => (current === 'running' ? 'fading' : current));
      }
    });
  }, [isLoading, phase]);

  useEffect(() => {
    if (!isWaitingForRouteContentCommit) {
      return undefined;
    }

    const startedAt = performance.now();
    const timeout = window.setTimeout(() => {
      emitRouteCommitStuck({
        entriesCount: entries.size,
        currentRouteContentCommitKey,
        durationMs: performance.now() - startedAt,
        hasCurrentRouteContentCommitted,
        hydrated,
        hydrationEffectsSettled,
        routeContentCommitKey,
      });
    }, LOADER_STUCK_WARNING_MS);

    return () => window.clearTimeout(timeout);
  }, [
    currentRouteContentCommitKey,
    entries.size,
    hasCurrentRouteContentCommitted,
    hydrated,
    hydrationEffectsSettled,
    isWaitingForRouteContentCommit,
    routeContentCommitKey,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent(phase === 'gone' ? ROOT_LOADER_DONE_EVENT : ROOT_LOADER_VISIBLE_EVENT),
    );
  }, [phase]);

  // Hand off from the pre-hydration surface pick (CSS !important overrides keyed
  // on this attribute) to React's inline styles. Layout-effect ordering keeps
  // the attribute in place through the first commit so opacity stays stable.
  useIsomorphicLayoutEffect(() => {
    document.documentElement.removeAttribute(ROOT_LOADER_INITIAL_SURFACE_ATTR);
  }, []);

  useEffect(() => {
    if (phase === 'gone') {
      sessionShowContextualSellLoaderRef.current = null;
    }
  }, [phase]);

  useEffect(() => {
    for (const blocker of getRootLoaderBlockersFromMetricList(blockersKey)) {
      sawBlockersRef.current.add(blocker);
    }
  }, [blockersKey]);

  useEffect(() => {
    if (phase !== 'running' || typeof window === 'undefined' || shownAtRef.current != null) {
      return;
    }

    const startedAt = hasShownLoaderBefore ? performance.now() : 0;
    hiddenTrackedRef.current = false;
    sawAddressPreviewRef.current = false;
    sawAddressUnavailableRef.current = false;
    sawMapLoadedRef.current = false;
    sawBlockersRef.current = new Set(getRootLoaderBlockersFromMetricList(blockersKey));
    shownAtRef.current = startedAt;

    trackRootLoaderAction(
      'shown',
      buildRootLoaderMetricPayload({
        duration_ms: undefined,
        elapsed_since_navigation_start_ms: Math.round(performance.now()),
        started_from_navigation_start: startedAt === 0,
      }),
    );
  }, [blockersKey, buildRootLoaderMetricPayload, hasShownLoaderBefore, phase]);

  useEffect(() => {
    if (phase !== 'running' || shownAtRef.current == null) {
      return;
    }

    if (addressPreviewStatus === 'preview' && !sawAddressPreviewRef.current) {
      sawAddressPreviewRef.current = true;
      trackRootLoaderAction(
        'address_preview',
        buildRootLoaderMetricPayload({
          has_map_coords: Boolean(coords),
          has_map_url: Boolean(mapUrl),
        }),
      );
    }

    if (addressPreviewStatus === 'unavailable' && !sawAddressUnavailableRef.current) {
      sawAddressUnavailableRef.current = true;
      trackRootLoaderAction('address_preview', buildRootLoaderMetricPayload());
    }
  }, [addressPreviewStatus, buildRootLoaderMetricPayload, coords, mapUrl, phase]);

  useEffect(() => {
    if (phase !== 'running' || !mapLoaded || sawMapLoadedRef.current) {
      return;
    }

    sawMapLoadedRef.current = true;
    trackRootLoaderAction('map_loaded', buildRootLoaderMetricPayload());
  }, [buildRootLoaderMetricPayload, mapLoaded, phase]);

  useEffect(() => {
    if (phase !== 'running' || typeof window === 'undefined') {
      return undefined;
    }

    const handlePageHide = () => {
      if (shownAtRef.current == null || hiddenTrackedRef.current) {
        return;
      }

      trackRootLoaderAction(
        'abandoned',
        buildRootLoaderMetricPayload({
          blockers_seen: getRootLoaderMetricList(sawBlockersRef.current),
          saw_address_preview: sawAddressPreviewRef.current,
          saw_address_unavailable: sawAddressUnavailableRef.current,
          saw_map_loaded: sawMapLoadedRef.current,
        }),
      );
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [buildRootLoaderMetricPayload, phase]);

  useEffect(() => {
    if (phase !== 'gone' || shownAtRef.current == null || hiddenTrackedRef.current) {
      return;
    }

    hiddenTrackedRef.current = true;
    setHasShownLoaderBefore(true);
    trackRootLoaderAction(
      'hidden',
      buildRootLoaderMetricPayload({
        blockers_seen: getRootLoaderMetricList(sawBlockersRef.current),
        saw_address_preview: sawAddressPreviewRef.current,
        saw_address_unavailable: sawAddressUnavailableRef.current,
        saw_map_loaded: sawMapLoadedRef.current,
      }),
    );
    shownAtRef.current = null;
  }, [buildRootLoaderMetricPayload, phase]);

  useIsomorphicLayoutEffect(() => {
    if (phase !== 'fading' || typeof window === 'undefined') return undefined;
    const timeout = window.setTimeout(() => {
      finishFade();
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [finishFade, phase]);

  const showAddressContent = showContextualSellLoader && hasAddress && isAddressContentRevealed;

  if (phase === 'gone') return null;

  return (
    <div
      id={ROOT_LOADER_ID}
      data-phase={phase}
      aria-live="polite"
      aria-atomic="true"
      aria-busy={phase === 'running'}
      onTransitionEnd={(event) => {
        if (event.propertyName === 'opacity' && phase === 'fading') {
          finishFade();
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: rootLoaderColors.mutedSurface,
        display: 'grid',
        placeItems: 'center',
        boxSizing: 'border-box',
        zIndex: 99999,
        overflow: 'auto',
        opacity: phase === 'fading' ? 0 : 1,
        transition: phase === 'fading' ? 'opacity 400ms ease-out' : 'none',
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
        fontFamily: rootLoaderFontFamily,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <style dangerouslySetInnerHTML={rootLoaderStylesheetMarkup} />
      <RootLoaderSurface
        // oxlint-disable-next-line react-hooks-js/refs -- Derived from sessionShowContextualSellLoaderRef (see session-latch above); ref access is intentional per that pattern.
        isVisible={!showContextualSellLoader}
        id="root-loader-simple-surface"
        variant="simple"
      >
        <RootLoaderSimpleState />
      </RootLoaderSurface>
      {/*
        Always rendered so the prerendered static shell includes the contextual
        surface. On a sell hard load the pre-hydration script in root.tsx flips
        opacity via CSS !important so contextual paints first — no simple flash.
      */}
      <RootLoaderSurface
        // oxlint-disable-next-line react-hooks-js/refs -- Derived from sessionShowContextualSellLoaderRef (see session-latch above); ref access is intentional per that pattern.
        isVisible={showContextualSellLoader}
        id="root-loader-contextual-surface"
        variant="contextual"
      >
        <div
          style={{
            position: 'absolute',
            top: 43,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <OpendoorLogo color={rootLoaderColors.brand} />
        </div>
        <div
          style={{
            width: rootLoaderContentWidth,
            paddingLeft: rootLoaderContentVisualInset,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              alignSelf: 'stretch',
              position: 'relative',
              minHeight: 236,
            }}
          >
            <div
              style={{
                position: showAddressContent ? 'absolute' : 'relative',
                inset: 0,
                opacity: showAddressContent ? 0 : 1,
                transform: showAddressContent ? 'scale(0.995)' : 'scale(1)',
                transition: `opacity 300ms ease-out, transform 420ms ${rootLoaderAddressEase}`,
                pointerEvents: 'none',
              }}
            >
              <RootLoaderAddressSkeleton />
            </div>
            {showAddressContent ? (
              <div
                style={{
                  alignSelf: 'stretch',
                  position: 'relative',
                  animation: 'cake-root-loader-content-in 380ms ease-out both',
                }}
              >
                <RootLoaderPropertyPreview
                  localityLine={localityLine}
                  streetLine={streetLine}
                  coords={coords}
                  mapLoaded={mapLoaded}
                  mapUrl={mapUrl}
                  onMapLoad={() => setMapLoaded(true)}
                />
              </div>
            ) : null}
            <RootLoaderChecklist />
          </div>
        </div>
      </RootLoaderSurface>
    </div>
  );
}

function emitRouteCommitStuck({
  currentRouteContentCommitKey,
  durationMs,
  entriesCount,
  hasCurrentRouteContentCommitted,
  hydrated,
  hydrationEffectsSettled,
  routeContentCommitKey,
}: {
  currentRouteContentCommitKey: string;
  durationMs: number;
  entriesCount: number;
  hasCurrentRouteContentCommitted: boolean;
  hydrated: boolean;
  hydrationEffectsSettled: boolean;
  routeContentCommitKey: string | null;
}): void {
  const key = 'root-loader:route-content-commit';
  if (!IS_PROD) {
    // eslint-disable-next-line no-console -- dev/staging diagnostic for root-loader commit gate stalls
    console.warn('[loader] stuck', key, `${durationMs.toFixed(0)}ms`, {
      currentRouteContentCommitKey,
      entriesCount,
      hasCurrentRouteContentCommitted,
      hydrated,
      hydrationEffectsSettled,
      routeContentCommitKey,
    });
  }
  globalTrackEvent('loader.stuck', 'root-loader', {
    data: {
      key,
      duration_ms: durationMs,
      current_route_content_commit_key: currentRouteContentCommitKey,
      entries_count: entriesCount,
      hydrated,
      hydration_effects_settled: hydrationEffectsSettled,
      route_content_committed: hasCurrentRouteContentCommitted,
      route_content_commit_key: routeContentCommitKey ?? '',
    },
  });
}

/* eslint-enable @opendoor/no-html-elements, react/forbid-dom-props, react/forbid-component-props */
